import _ from 'lodash'
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { SyncDeviceToCloudOrdinalsStatus, SyncItemStatus, syncItemStatusIsError, syncItemStatusIsPending, SyncResultStatus } from '@fusion-advantage/formlib'
import { DateTime } from 'luxon'
import { AppRootState } from './appStore'
import { SyncResult } from './syncthunk/SyncResult'
import { err, ok } from 'neverthrow'
import { SyncConfig } from './syncthunk/SyncConfig'
import { SyncItemKey, SyncItemStateResult, SyncItemSubtaskType, SyncItemType } from './syncthunk/SyncItemResult'

export interface SyncItemState {
	key: SyncItemKey
	startedAt: number
	result: SyncItemStateResult
	subtasks: {
		type: SyncItemSubtaskType
		endedAt: number
	}[]
}

export interface SyncSliceState {
	startedAt: number|null
	canCancel: boolean
	endedAt: number|null
	config: SyncConfig|null
	denominatorEstimate: number
	syncItems: SyncItemState[]
	// do not delete these items as they have been selected for imminent sync
	pendingDeviceToCloudOrdinals: SyncDeviceToCloudOrdinalsStatus.Querying|number[]
	notice: string|null
}

const initialState: SyncSliceState = {
	startedAt: null,
	canCancel: false,
	endedAt: null,
	config: null,
	denominatorEstimate: 0,
	syncItems: [],
	pendingDeviceToCloudOrdinals: [],
	notice: null,
}

const syncSlice = createSlice({
	name: 'sync',
	initialState,
	reducers: {
		setSyncStart(state, action: PayloadAction<SyncConfig>) {
			return {
				...initialState,
				startedAt: Date.now(),
				config: action.payload,
			}
		},
		setSyncCanCancel(state, action: PayloadAction<boolean>) {
			state.canCancel = action.payload
		},
		setSyncEnd(state) {
			state.endedAt = Date.now()
			state.pendingDeviceToCloudOrdinals = []
		},
		incrementSyncDenominatorEstimate(state, action: PayloadAction<number>) {
			state.denominatorEstimate = action.payload
		},
		setSyncItemResult(state, action: PayloadAction<{key: SyncItemKey, stateResult: SyncItemStateResult}>) {
			const { key, stateResult } = action.payload
			const itemIdx = selectSyncItemIdx(state, key)
			if(itemIdx < 0) {
				state.syncItems.push({
					key,
					result: stateResult,
					subtasks: [],
					startedAt: Date.now(),
				})
			} else {
				state.syncItems[itemIdx].result = stateResult
			}
		},
		setSyncItemSubtaskTick(state, action: PayloadAction<{key: SyncItemKey, subtaskType: SyncItemSubtaskType}>) {
			const { key, subtaskType } = action.payload
			let itemIdx = selectSyncItemIdx(state, key)
			if(itemIdx < 0) {
				itemIdx = state.syncItems.length
				state.syncItems.push({
					key,
					result: { status: SyncItemStatus.Pending },
					subtasks: [],
					startedAt: DateTime.now().toMillis(),
				})
			}
			const subtask = {
				type: subtaskType,
				endedAt: DateTime.now().toMillis(),
			}
			state.syncItems[itemIdx].subtasks.push(subtask)
		},
		setSyncPendingDeviceToCloudOrdinals(state, action: PayloadAction<SyncSliceState["pendingDeviceToCloudOrdinals"]>) {
			state.pendingDeviceToCloudOrdinals = action.payload
		},
		dropSyncPendingDeviceToCloudOrdinal(state, action: PayloadAction<number>) {
			if(state.pendingDeviceToCloudOrdinals !== SyncDeviceToCloudOrdinalsStatus.Querying) {
				const pendingDeviceToCloudOrdinals = state.pendingDeviceToCloudOrdinals.filter(o => o !== action.payload)
				return {
					...state,
					pendingDeviceToCloudOrdinals,
				}
			}
		},
		setSyncNotice(state, action: PayloadAction<string|null>) {
			state.notice = action.payload
		},
	}
})

export const {
	setSyncStart,
	setSyncCanCancel,
	setSyncEnd,
	incrementSyncDenominatorEstimate,
	setSyncItemResult,
	setSyncItemSubtaskTick,
	setSyncPendingDeviceToCloudOrdinals,
	dropSyncPendingDeviceToCloudOrdinal,
	setSyncNotice,
} = syncSlice.actions
export default syncSlice.reducer

const selectSyncItemIdx = (state: SyncSliceState, key: SyncItemKey) =>
	state.syncItems.findIndex(item => _.isEqual(item.key, key))

export const selectSyncItem = (state: SyncSliceState, key: SyncItemKey) =>
	state.syncItems.at(selectSyncItemIdx(state, key))

export const selectSyncResult = createSelector(
	(state: AppRootState) => state.sync,
	(sync): SyncResult => {
		if(sync.startedAt && sync.endedAt === null) {
			return err({ status: SyncResultStatus.Pending })
		}
		const appStatusKey: SyncItemKey = { type: SyncItemType.AppStatus }
		const appStatusState = selectSyncItem(sync, appStatusKey)
		if(!appStatusState) {
			return err({
				status: SyncResultStatus.Error,
				item: appStatusKey,
			})
		}
		if(appStatusState.result.status === SyncItemStatus.NoConnection) {
			return err({ status: SyncResultStatus.NoConnection })
		}
		if(appStatusState.result.status === SyncItemStatus.NoAuth) {
			return err({ status: SyncResultStatus.NoAuth })
		}
		for(let item of sync.syncItems) {
			if(syncItemStatusIsError(item.result.status)) {
				return err({
					status: SyncResultStatus.Error,
					item,
				})
			}
			if(syncItemStatusIsPending(item.result.status)) {
				return err({
					status: SyncResultStatus.Pending
				})
			}
		}
		return ok()
	}
)

export const selectSyncResultStatus = createSelector(
	(state: AppRootState) => selectSyncResult(state),
	(state: AppRootState) => state.dashboardData.pendingDeviceToCloudCount,
	(syncResult, pendingDeviceToCloudCount): SyncResultStatus => {
		if(syncResult.isErr()) {
			return syncResult.error.status
		}
		// since right now the only thing using this selector is the indicator
		// in the dashboard header, this is a simple hack that causes the
		// sync indicator to turn red whenever there's any pending
		// deviceToCloud item waiting to be sync'd.  so at a glance clinicians
		// can see wether sync may need their attention (error or pending
		// uploads = red) or not (success and no pending uploads = blue).
		// thanks to ryane for helping think through this enhancement.
		if(pendingDeviceToCloudCount > 0) {
			return SyncResultStatus.Error
		}
		return SyncResultStatus.Ok
	}
)
